import * as iotServicesSdk from "iot-services-sdk";
import axios from "axios";

const state = {
  certificates: [],
  currentCertificate: null,
  loading: false,
  error: null,
  baseUrl: null,
  authorizer: null,
  region: null,
  service: null,
};

const getters = {
  allCertificates: (state) => state.certificates,
  certificateById: (state) => (id) =>
    state.certificates.find((cert) => cert.certificate_id === id),
  certificatesByDevice: (state) => (deviceId) =>
    state.certificates.filter((cert) => cert.device_id === deviceId),
  activeCertificates: (state) =>
    state.certificates.filter((cert) => cert.status === "active"),
  certificateCount: (state) => state.certificates.length,
};

const mutations = {
  init(state, {baseUrl, authorizer, region, service}) {
    state.baseUrl = baseUrl;
    state.authorizer = authorizer;
    state.region = region;
    state.service = service;
  },

  setCertificates(state, certificates) {
    state.certificates = certificates;
  },

  addCertificate(state, certificate) {
    state.certificates.push(certificate);
  },

  updateCertificate(state, updatedCertificate) {
    const index = state.certificates.findIndex(
      (cert) => cert.certificate_id === updatedCertificate.certificate_id,
    );
    if (index !== -1) {
      state.certificates.splice(index, 1, updatedCertificate);
    }
  },

  removeCertificate(state, certificateId) {
    state.certificates = state.certificates.filter(
      (cert) => cert.certificate_id !== certificateId,
    );
  },

  setCurrentCertificate(state, certificate) {
    state.currentCertificate = certificate;
  },

  setLoading(state, loading) {
    state.loading = loading;
  },

  setError(state, error) {
    state.error = error;
  },

  clearError(state) {
    state.error = null;
  },
};

const actions = {
  init({rootState, commit}) {
    const httpInfo = rootState.metadata.backendApi.http.prod;
    const baseUrl = "https://" + httpInfo.host + "/" + httpInfo.basePath;
    const service = httpInfo.service;
    const region = httpInfo.region;
    const authorizer = new iotServicesSdk.shared.auth.sigv4.SigV4Authorizer(
      service,
      region,
    );
    commit("init", {baseUrl, authorizer, region, service});
  },

  async fetchCertificates({rootState, state, commit}) {
    commit("setLoading", true);
    commit("clearError");
    try {
      const url = new URL("/v1/certificates", state.baseUrl);
      const sigv4Descriptor = new iotServicesSdk.shared.auth.sigv4.SigV4RequestDescriptor(
        rootState.authentication.credentials,
        "GET",
        url.hostname,
        url.pathname,
        url.search,
        "",
        state.service,
        state.region,
      );
      const authHeaders = state.authorizer.authorize(sigv4Descriptor);
      const response = await axios.get(url.href, {headers: authHeaders});
      commit("setCertificates", response.data);
      return response.data;
    } catch (error) {
      commit("setError", error.message);
      throw error;
    } finally {
      commit("setLoading", false);
    }
  },

  async getCertificate({rootState, state, commit}, certificateId) {
    commit("setLoading", true);
    commit("clearError");
    try {
      const url = new URL("/v1/certificates/" + certificateId, state.baseUrl);
      const sigv4Descriptor = new iotServicesSdk.shared.auth.sigv4.SigV4RequestDescriptor(
        rootState.authentication.credentials,
        "GET",
        url.hostname,
        url.pathname,
        url.search,
        "",
        state.service,
        state.region,
      );
      const authHeaders = state.authorizer.authorize(sigv4Descriptor);
      const response = await axios.get(url.href, {headers: authHeaders});
      commit("setCurrentCertificate", response.data);
      return response.data;
    } catch (error) {
      commit("setError", error.message);
      throw error;
    } finally {
      commit("setLoading", false);
    }
  },

  async createCertificate({rootState, state, commit}, certificateData) {
    commit("setLoading", true);
    commit("clearError");
    try {
      const url = new URL("/v1/certificates", state.baseUrl);
      const body = JSON.stringify(certificateData);
      const sigv4Descriptor = new iotServicesSdk.shared.auth.sigv4.SigV4RequestDescriptor(
        rootState.authentication.credentials,
        "POST",
        url.hostname,
        url.pathname,
        url.search,
        body,
        state.service,
        state.region,
      );
      const authHeaders = state.authorizer.authorize(sigv4Descriptor);
      const response = await axios.post(url.href, certificateData, {
        headers: authHeaders,
      });
      commit("addCertificate", response.data);
      return response.data;
    } catch (error) {
      commit("setError", error.message);
      throw error;
    } finally {
      commit("setLoading", false);
    }
  },

  async updateCertificate(
    {rootState, state, commit},
    {certificateId, certificateData},
  ) {
    commit("setLoading", true);
    commit("clearError");
    try {
      const url = new URL("/v1/certificates/" + certificateId, state.baseUrl);
      const body = JSON.stringify(certificateData);
      const sigv4Descriptor = new iotServicesSdk.shared.auth.sigv4.SigV4RequestDescriptor(
        rootState.authentication.credentials,
        "PUT",
        url.hostname,
        url.pathname,
        url.search,
        body,
        state.service,
        state.region,
      );
      const authHeaders = state.authorizer.authorize(sigv4Descriptor);
      const response = await axios.put(url.href, certificateData, {
        headers: authHeaders,
      });
      commit(
        "updateCertificate",
        response.data || {certificate_id: certificateId, ...certificateData},
      );
      return response.data;
    } catch (error) {
      commit("setError", error.message);
      throw error;
    } finally {
      commit("setLoading", false);
    }
  },

  async deleteCertificate({rootState, state, commit}, certificateId) {
    commit("setLoading", true);
    commit("clearError");
    try {
      const url = new URL("/v1/certificates/" + certificateId, state.baseUrl);
      const sigv4Descriptor = new iotServicesSdk.shared.auth.sigv4.SigV4RequestDescriptor(
        rootState.authentication.credentials,
        "DELETE",
        url.hostname,
        url.pathname,
        url.search,
        "",
        state.service,
        state.region,
      );
      const authHeaders = state.authorizer.authorize(sigv4Descriptor);
      await axios.delete(url.href, {headers: authHeaders});
      commit("removeCertificate", certificateId);
      return true;
    } catch (error) {
      commit("setError", error.message);
      throw error;
    } finally {
      commit("setLoading", false);
    }
  },

  async downloadCertificate({rootState, state, commit}, certificateId) {
    commit("setLoading", true);
    commit("clearError");
    try {
      const url = new URL(
        "/v1/certificates/" + certificateId + "/download",
        state.baseUrl,
      );
      const sigv4Descriptor = new iotServicesSdk.shared.auth.sigv4.SigV4RequestDescriptor(
        rootState.authentication.credentials,
        "POST",
        url.hostname,
        url.pathname,
        url.search,
        "",
        state.service,
        state.region,
      );
      const authHeaders = state.authorizer.authorize(sigv4Descriptor);
      const response = await axios.post(
        url.href,
        {},
        {headers: authHeaders, responseType: "blob"},
      );
      return response.data;
    } catch (error) {
      commit("setError", error.message);
      throw error;
    } finally {
      commit("setLoading", false);
    }
  },

  clearError({commit}) {
    commit("clearError");
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
